import type {ReactElement} from 'react';
import React, {useContext, useEffect, useState} from 'react';
import {Button, Size, Type} from '@Components/button';
import styles from './publishing-qr-code-panel.module.scss';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import type {QRCodeRenderersOptions} from 'qrcode';
import QRCode from 'qrcode';
import {Text, TextSize} from '@Components/text';
import {confirmationPanelFlow, setErrorMessageForPublishingPanel, setWebLinksData} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {useAppDispatch} from '@/hooks';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {openErrorModal} from '@Modals/error-modal';
import {useHashedId, usePrintProduct, useWebLinks} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {getUserPremiumLevel} from '@Libraries/user.library';
import type {PosterProductDataForProcessDesign} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {DisplayType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {ModalDetails} from '@Components/modal/modal-context';
import {PublishingCopyQrcode} from '@Panels/poster-publishing-options-panel/components/publishing-copy-qrcode/publishing-copy-qrcode';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';
import {isMobile} from 'react-device-detect';
import {SkeletonBrick} from '@Components/skeleton-brick';
import type {WebLinkResponse} from '@Panels/poster-publishing-options-panel/components/publishing-social-media-panel/publishing-social-media-panel.types';
import {updatePanelSizeData} from '@Components/panel/panel-reducer';
import {CircularProgressLoader} from '@Components/circular-progress-loader';
import {noop} from '@Utils/general.util';
import {getAssetUrl} from '@Utils/s3.util';
import {readCookie} from '@Utils/cookie.util';
import {getEditPosterURL} from '@Libraries/poster-editor.library';
import {createPosterForQRCodeTemplate} from '@PosterWhiteboard/poster/poster-init';

const GENERATED_QR_CODE_WIDTH = 130;
const GENERATED_QR_CODE_MARGIN = 0;
const GENERATED_QR_CODE_CORRECTION_LEVEL = 'M';
const REDIRECT_CONFIRMATION_QR_TEMPLATE_COOKIE_NAME = 'pmwrcqrt';

interface PublishingQRCodePanelProps {
  displayType: DisplayType;
  hashedId?: string;
  panelId?: string;
  showQrTemplateSection?: boolean;
  onTogglePublish?: () => void;
}

export function PublishingQRCodePanel({displayType, onTogglePublish = noop, showQrTemplateSection = true, ...props}: PublishingQRCodePanelProps): ReactElement {
  const [isQRGenerating, setIsQRGenerating] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [qrCode, setQRCode] = useState<string>();
  const webLink = useWebLinks();
  const modalDetails = useContext(ModalDetails);
  const inMobile = modalDetails?.isMobileModal;
  const posterHashedId = useHashedId();
  const userPremiumLevel = getUserPremiumLevel();
  const [showLoader, setShowLoader] = useState(false);
  const printProduct: PosterProductDataForProcessDesign | undefined = usePrintProduct();
  const dispatch = useAppDispatch();
  const inModal = displayType === DisplayType.MODAL;

  const hashedIdFromStore = useHashedId();
  const hashedId = props.hashedId ?? hashedIdFromStore;

  const updateModalHeight = (): void => {
    if (props.panelId) {
      let height: string;
      if (isMobile) {
        if (showQrTemplateSection) {
          height = '535px';
        } else {
          height = '370px';
        }
      } else {
        if (showQrTemplateSection) {
          height = '625px';
        } else {
          height = '410px';
        }
      }
      dispatch(
        updatePanelSizeData({
          sizeDataId: props.panelId,
          sizeData: {
            height: height,
          },
        })
      );
    }
  };

  useEffect(() => {
    if (!showLoader && ((!webLink.qrHostedShareableUrl && hashedId) || hashedIdFromStore !== hashedId)) {
      setShowLoader(true);
      void fetchInitPublishQrLink();
    } else {
      void updateQrCode(webLink.qrHostedShareableUrl);
    }
    updateModalHeight();
  }, []);

  const fetchInitPublishQrLink = async (): Promise<void> => {
    try {
      const response = (await window.PMW.readLocal('poster/getQrHostedPosterLinkInfo', {hashedId: hashedId})) as WebLinkResponse;
      dispatch(
        setWebLinksData({
          qrHostedShareableUrl: response.qrHostedShareableUrl,
          qrTemplateHashedId: response.qrTemplateHashedId,
          isQrHostedLinkPublished: response.isQrHostedLinkPublished,
        })
      );
      await updateQrCode(response.qrHostedShareableUrl);
      setShowLoader(false);
    } catch {
      dispatch(setErrorMessageForPublishingPanel(window.i18next.t('pmwjs_publish_webpage_error')));
      setShowLoader(false);
    }
  };

  const getQRCodeRenderOptions = (): QRCodeRenderersOptions => {
    return {
      width: GENERATED_QR_CODE_WIDTH,
      margin: GENERATED_QR_CODE_MARGIN,
      errorCorrectionLevel: GENERATED_QR_CODE_CORRECTION_LEVEL,
    };
  };

  const updateQrCode = async (url: string): Promise<void> => {
    const qrCodeString = await QRCode.toString(url, getQRCodeRenderOptions());
    setQRCode(qrCodeString);
  };

  const downloadQRCode = async (): Promise<void> => {
    trackGA4CustomEvent(GA4EventName.PUBLISHING_DOWNLOAD_QR_CODE);
    const qrDataUrl = await QRCode.toDataURL(webLink.qrHostedShareableUrl, getQRCodeRenderOptions());
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = 'QR-Code.png';
    link.click();
    link.remove();
  };

  const onGenerateQRCode = async (): Promise<void> => {
    trackGA4CustomEvent(GA4EventName.PUBLISHING_GENERATE_QR_CODE);
    setIsQRGenerating(true);
    try {
      await window.PMW.writeLocal('poster/publishQRHostedPosterLink', {
        hashedId: posterHashedId,
      });
      dispatch(setWebLinksData({isQrHostedLinkPublished: true}));
      onTogglePublish();
    } catch {
      openErrorModal({log: false});
    }
    setIsQRGenerating(false);
  };

  const unpublishQrHostedLink = async (): Promise<void> => {
    try {
      setIsUnpublishing(true);
      await window.PMW.writeLocal('poster/unpublishQRHostedPosterLink', {
        hashedId: hashedId,
        qrTemplateHashedId: webLink.qrTemplateHashedId,
      });
      dispatch(
        setWebLinksData({
          isQrHostedLinkPublished: false,
          qrTemplateHashedId: '',
        })
      );
      onTogglePublish();
    } catch {
      openErrorModal({log: false});
    }
    setIsUnpublishing(false);
  };

  const openTemplateDesign = (templateHashedId: string): void => {
    if (inMobile && readCookie(REDIRECT_CONFIRMATION_QR_TEMPLATE_COOKIE_NAME) !== '1') {
      dispatch(confirmationPanelFlow());
    } else {
      window.open(getEditPosterURL(templateHashedId), '_blank');
    }
  };

  const onUseQrTemplateClick = async (): Promise<void> => {
    if (webLink.qrTemplateHashedId) {
      openTemplateDesign(webLink.qrTemplateHashedId);
      return;
    }
    trackGA4CustomEvent(GA4EventName.PUBLISHING_QUICK_QR_TEMPLATE);
    setIsQRGenerating(true);
    try {
      const templateHashedId = await createPosterForQRCodeTemplate(posterHashedId);
      if (templateHashedId) {
        dispatch(setWebLinksData({qrTemplateHashedId: templateHashedId}));
        openTemplateDesign(templateHashedId);
      } else {
        openErrorModal({log: false});
      }
    } catch {
      openErrorModal({log: false});
    }
    setIsQRGenerating(false);
  };

  const getSuccessMessage = (): ReactElement | null => {
    if (!webLink.isQrHostedLinkPublished) {
      return null;
    }
    return <Text val={window.i18next.t('pmwjs_qr_code_live_and_ready_for_use')} size={TextSize.XSMALL} className={'content-body spacing-p-t-1'} />;
  };

  const getQRSection = (): ReactElement => {
    return (
      <div className={`flex-center content-sub-text ${styles.qrContainer} ${webLink.isQrHostedLinkPublished ? 'spacing-m-t-4' : ''} spacing-m-b-4 radius-8 _full-width`}>
        <div className={`${styles.qrCodeWithLogo} flex-center radius-8`}>
          {qrCode ? <div className={styles.qrCode} dangerouslySetInnerHTML={{__html: qrCode}}></div> : null}
          {!webLink.isQrHostedLinkPublished ? <div className={`_full-width _full-height radius-8 ${styles.overlay}`}></div> : null}
        </div>
        {webLink.isQrHostedLinkPublished ? <PublishingCopyQrcode /> : null}
      </div>
    );
  };

  const getWatermarkInfoForPayGUser = (): ReactElement | null => {
    if (userPremiumLevel === USER_PREMIUM_LEVELS.PAYG && printProduct?.needsPurchase) {
      return (
        <div className="flex-v-row spacing-p-b-4">
          <Text val={window.i18next.t('pmwjs_remove_watermark')} bold size={TextSize.XXSMALL} className="spacing-m-b-1 content-body" />
          <Text
            val={window.i18next.t('pmwjs_pay_amount_or_upgrade_premium', {price: printProduct?.price, redirectionLink: window.PMW.util.site_url('/premium')})}
            size={TextSize.XXSMALL}
            dangerouslySetInnerHTML
            className="content-body"
          />
        </div>
      );
    }
    return null;
  };

  const getBottomCTAs = (): ReactElement | null => {
    return (
      <div className={`flexbox`}>
        {getUnpublishCTA()}
        {getGenerateOrDownloadQRCTA()}
      </div>
    );
  };

  const getUnpublishCTA = (): ReactElement | null => {
    if (!webLink.isQrHostedLinkPublished) {
      return null;
    }
    return (
      <Button
        type={Type.SECONDARY}
        text={window.i18next.t('pmwjs_unpublish')}
        size={Size.SMALL}
        icon={'icon-unlink'}
        customClasses={'spacing-m-r-2'}
        isFullWidth
        isLoading={isUnpublishing}
        onClick={(): void => {
          void unpublishQrHostedLink();
        }}
      />
    );
  };

  const getGenerateOrDownloadQRCTA = (): ReactElement => {
    return (
      <Button
        type={Type.PRIMARY}
        icon={'icon-download'}
        text={webLink.isQrHostedLinkPublished ? window.i18next.t('pmwjs_download_qr_code') : window.i18next.t('pmwjs_generate_qr_code')}
        size={Size.SMALL}
        customClasses={`-noun`}
        isFullWidth
        isLoading={isQRGenerating && !webLink.isQrHostedLinkPublished}
        onClick={webLink.isQrHostedLinkPublished ? downloadQRCode : onGenerateQRCode}
      />
    );
  };

  const getQRTemplateSection = (): ReactElement | null => {
    if (!showQrTemplateSection) {
      return null;
    }
    if (!webLink.isQrHostedLinkPublished) {
      return null;
    }
    if (inMobile || inModal) {
      return (
        <div>
          <div className={`${styles.divider} ${inMobile ? 'spacing-m-r-6 spacing-m-l-6' : 'spacing-m-t-4 spacing-m-b-4 spacing-m-r-12 spacing-m-l-12'}`}></div>
          {getQRTemplatePanel()}
        </div>
      );
    }
    return (
      <Dropdown
        selector={<div />}
        popup={getQRTemplatePanel()}
        topOffset={-4}
        open={webLink.isQrHostedLinkPublished}
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        position={DROPDOWN_POSITION.FIXED}
        doCloseOnInnerClick={false}
        doesCloseOnOuterClick={false}
      />
    );
  };

  const getGenerateQRTemplateInfo = (): ReactElement => {
    return (
      <Text
        val={window.i18next.t('pmwjs_qr_template_use_it')}
        className={`spacing-m-l-1 ${isQRGenerating ? 'content-disabled' : 'content-primary'} ${styles.generateText}`}
        disableSelection={isQRGenerating}
        size={TextSize.XSMALL}
        dangerouslySetInnerHTML
        onClick={(): void => {
          void onUseQrTemplateClick();
        }}
      />
    );
  };

  const getQRTemplatePanel = (): ReactElement => {
    return (
      <div className={inMobile ? 'spacing-p-4' : inModal ? 'spacing-p-b-8 spacing-p-r-12 spacing-p-l-12' : 'spacing-p-t-2 spacing-p-b-3 spacing-p-l-3 spacing-p-r-3'}>
        <div className={'flex-row-align-center'}>
          <Text val={window.i18next.t('pmwjs_generated_qr_template_desc')} className={'content-body'} size={TextSize.XSMALL} />
          {getGenerateQRTemplateInfo()}
        </div>
        <div className={`flex-center ${styles.qrTemplateContainer} flex-center spacing-m-t-4 radius-8 _full-width`}>
          <div className={`radius-4 ${styles.qrTemplate}`}>
            {isQRGenerating ? (
              <SkeletonBrick className={'_full-height'} />
            ) : (
              <Thumbnail className={'radius-4'} data={{type: ThumbnailType.IMAGE, imageSrc: getAssetUrl('published-qr-template.png')}} />
            )}
          </div>
        </div>
      </div>
    );
  };

  const getClassesForContainer = (): string => {
    if (inMobile) {
      return 'spacing-p-t-1 spacing-p-l-4 spacing-p-r-4 spacing-p-b-4';
    }
    if (inModal) {
      return `spacing-p-t-8 spacing-p-r-12 spacing-p-l-12 ${webLink.isQrHostedLinkPublished ? '' : 'spacing-p-b-8'}`;
    }
    return 'spacing-p-l-3 spacing-p-r-3 spacing-p-b-3';
  };

  if (showLoader) {
    return (
      <div className={'flex-center _full-width _full-height'}>
        <div className={'flex-center'}>
          <CircularProgressLoader />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={getClassesForContainer()}>
        {getSuccessMessage()}
        {getQRSection()}
        {getWatermarkInfoForPayGUser()}
        {getBottomCTAs()}
      </div>
      {getQRTemplateSection()}
    </>
  );
}
